import React from "react";
import { Popover, Typography } from "antd";
import { MdOutlineSmsFailed } from "react-icons/md";
import { BsCheck2, BsCheck2All } from "react-icons/bs";
import { ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Text } = Typography;

/**
 * The chat bubble itself — alignment, colour, corners, timestamp and ticks.
 * Channel-specific content goes in as children, so WhatsApp media and email
 * bodies share one piece of chrome instead of two that drift apart.
 */

export const StatusTick = ({ status, visible }) => {
  if (!visible) return null;
  if (isFailed(status))
    return <MdOutlineSmsFailed style={{ color: "#ff4d4f", marginLeft: 3, fontSize: 13 }} />;
  if (status === "read")
    return <BsCheck2All style={{ color: "#53bdeb", marginLeft: 3, fontSize: 13 }} />;
  if (status === "delivered")
    return <BsCheck2All style={{ color: "#8696a0", marginLeft: 3, fontSize: 13 }} />;
  return <BsCheck2 style={{ color: "#8696a0", marginLeft: 3, fontSize: 13 }} />;
};

const isFailed = (status) => status === "failed" || status === "FAILED" || status === "BOUNCED";

const infoTime = (value) => (value ? dayjs(value).format("DD MMM YYYY, hh:mm A") : "—");

/**
 * WhatsApp's "Message info": when it was sent, delivered and read, or why it
 * failed. `receipts` is `{sent, delivered, read}` from the envelope's meta.
 */
export const MessageInfo = ({
  status,
  receipts = {},
  errorMessage,
  sentAt,
  // Email has no delivered/read receipts, so it only shows when it was sent.
  trackDelivery = true,
}) => {
  const rows = [
    { key: "read", label: "Read", icon: <BsCheck2All style={{ color: "#53bdeb" }} /> },
    { key: "delivered", label: "Delivered", icon: <BsCheck2All style={{ color: "#8696a0" }} /> },
    { key: "sent", label: "Sent", icon: <BsCheck2 style={{ color: "#8696a0" }} /> },
  ].filter((row) => trackDelivery || row.key === "sent");
  return (
    <div style={{ minWidth: 240, maxWidth: 320 }}>
      {isFailed(status) && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#ff4d4f", fontWeight: 500 }}>
            <MdOutlineSmsFailed /> Not delivered
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {errorMessage || "Meta reported the message as failed but gave no reason."}
          </Text>
        </div>
      )}
      {String(status || "").toLowerCase() === "queued" && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <ClockCircleOutlined style={{ color: "#8696a0" }} />
          <Text type="secondary" style={{ fontSize: 12 }}>Waiting to be sent</Text>
        </div>
      )}
      {rows.map(({ key, label, icon }) => {
        // Before the first receipt arrives, the send time is our own.
        const value = receipts?.[key] || (key === "sent" ? sentAt : null);
        return (
          <div
            key={key}
            style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 0" }}
          >
            <span style={{ fontSize: 16, lineHeight: "18px" }}>{icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {infoTime(value)}
              </Text>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DELIVERY_LABELS = {
  queued: "Queued",
  sent: "Sent",
  delivered: "Delivered",
  read: "Read",
  failed: "Failed",
  bounced: "Bounced",
};

/**
 * Tick(s) plus a word — Sent / Delivered / Read / Failed — for places outside
 * the chat bubble, such as a lead's timeline. Click for the message info.
 */
export const DeliveryStatus = ({ status, receipts, errorMessage, sentAt, trackDelivery = true }) => {
  const key = String(status || "sent").toLowerCase();
  const failed = isFailed(key);
  const color = failed ? "#ff4d4f" : key === "read" ? "#53bdeb" : "#8696a0";
  return (
    <Popover
      title="Message info"
      trigger="click"
      placement="bottomRight"
      content={
        <MessageInfo
          status={key}
          receipts={receipts}
          errorMessage={errorMessage}
          sentAt={sentAt}
          trackDelivery={trackDelivery}
        />
      }
    >
      <span
        role="button"
        tabIndex={0}
        title="Message info"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          cursor: "pointer",
          fontSize: 12,
          color,
          whiteSpace: "nowrap",
        }}
      >
        {key === "queued" ? (
          <ClockCircleOutlined style={{ fontSize: 11, marginRight: 2 }} />
        ) : (
          <StatusTick status={key} visible />
        )}
        {DELIVERY_LABELS[key] || status}
      </span>
    </Popover>
  );
};

export const BubbleMeta = ({ timestamp, status, isOutbound, overlayOnMedia, info }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 2,
      marginTop: 2,
      ...(overlayOnMedia
        ? {
            position: "absolute",
            bottom: 4,
            right: 6,
            background: "rgba(0,0,0,0.45)",
            borderRadius: 8,
            padding: "1px 6px",
          }
        : {}),
    }}
  >
    <span
      style={{
        color: overlayOnMedia ? "#fff" : "#8696a0",
        fontSize: 10.5,
        whiteSpace: "nowrap",
      }}
    >
      {dayjs(timestamp).format("HH:mm")}
    </span>
    {isOutbound && info ? (
      <Popover title="Message info" trigger="click" placement="topRight" content={info}>
        <span
          role="button"
          tabIndex={0}
          title="Message info"
          style={{ cursor: "pointer", display: "inline-flex" }}
        >
          <StatusTick status={status} visible />
        </span>
      </Popover>
    ) : (
      <StatusTick status={status} visible={isOutbound} />
    )}
  </div>
);

const MessageBubble = ({
  id,
  isOutbound,
  palette,
  dense = false,
  maxWidth = 520,
  header,
  footer,
  reaction,
  aside,
  onMouseEnter,
  onMouseLeave,
  children,
}) => (
  <div
    id={id}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 4,
      justifyContent: isOutbound ? "flex-end" : "flex-start",
      marginBottom: reaction ? 18 : 6,
      paddingInline: 4,
    }}
  >
    {/* `aside` (e.g. a reply button) sits on the side facing the thread's middle. */}
    {isOutbound ? aside : null}
    <div
      style={{
        position: "relative",
        maxWidth,
        minWidth: 90,
        background: isOutbound ? palette.outboundBubble : palette.inboundBubble,
        borderRadius: isOutbound ? "8px 8px 2px 8px" : "8px 8px 8px 2px",
        boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)",
        padding: dense ? 3 : "6px 8px",
      }}
    >
      {header}
      {/* overflow lives on the inner wrapper so media corners stay rounded
          while a reaction can still hang below the bubble */}
      <div style={{ overflow: "hidden", borderRadius: 6 }}>{children}</div>
      {footer}
      {reaction ? (
        <div
          style={{
            position: "absolute",
            bottom: -12,
            [isOutbound ? "right" : "left"]: 8,
            background: "#fff",
            borderRadius: 12,
            padding: "0 5px",
            fontSize: 13,
            boxShadow: "0 1px 2px rgba(11,20,26,0.2)",
          }}
        >
          {reaction}
        </div>
      ) : null}
    </div>
    {isOutbound ? null : aside}
  </div>
);

export const Divider = ({ label, icon }) => (
  <div style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
    <Text
      style={{
        background: "rgba(255,255,255,0.92)",
        borderRadius: 8,
        padding: "3px 12px",
        fontSize: 11.5,
        color: "#54656f",
        boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {icon}
      {label}
    </Text>
  </div>
);

export default MessageBubble;
